<template>
  <transition name="slide">
    <div v-if="visible" class="chatbot-container">
      <!-- Header -->
      <div class="chatbot-header">
        <span>AI Assistant</span>
        <button class="close-btn" @click="closeChat">×</button>
      </div>

      <!-- Body -->
      <div class="chatbot-body" ref="chatBody" @scroll="handleScroll">
        <div
          v-for="msg in messages"
          :key="msg.id"
          class="chat-message"
          :class="msg.type"
        >
          <p v-if="!msg.loading">{{ msg.text }}</p>
          <div v-else class="loading-dots">
            <span></span><span></span><span></span>
          </div>
        </div>
      </div>

      <!-- Scroll Button -->
      <button
        v-if="showScrollButton"
        class="scroll-bottom-btn"
        @click="scrollToBottom"
      >
        ⬇
      </button>

      <!-- Footer -->
      <div class="chatbot-footer">
        <input
          type="text"
          v-model="userQuestion"
          @keyup.enter="sendMessage"
          placeholder="Type your question..."
        />
        <button @click="sendMessage">Send</button>
      </div>
    </div>
  </transition>
</template>

<script>
import { EventBus } from "@/helpers/event-bus";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  data() {
    return {
      visible: false,
      messages: [],
      userQuestion: "",
      isLoading: false,
      showScrollButton: false,
    };
  },

  created() {
    EventBus.$on("toggle-chatbot", () => {
      this.visible = !this.visible;
      if (this.visible) {
        this.loadChatHistory();
        EventBus.$emit("chatbot-opened");
      } else {
        EventBus.$emit("chatbot-closed");
      }
    });
  },

  methods: {
    closeChat() {
      this.visible = false;
      EventBus.$emit("chatbot-closed");
    },

    async loadChatHistory() {
      try {
        const res = await this.$api.get("/api/chat/history");
        this.messages = res.data.logs
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
          .flatMap((log) => [
            { id: `q-${log.id}`, text: log.question, type: "user" },
            {
              id: `a-${log.id}`,
              text: log.answer,
              type: "ai",
              sources: log.sources,
            },
          ]);
        this.scrollToBottom();
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error loading history",
            icon: "BellIcon",
            variant: "danger",
            text: error?.response?.data?.message,
          },
        });
      }
    },

    async sendMessage() {
      if (!this.userQuestion.trim()) return;

      this.messages.push({
        id: Date.now(),
        text: this.userQuestion,
        type: "user",
      });

      const question = this.userQuestion;
      this.userQuestion = "";

      this.isLoading = true;
      this.messages.push({
        id: "loading",
        text: "AI is typing...",
        type: "ai",
        loading: true,
      });

      this.scrollToBottom();

      try {
        const res = await this.$api.post("/api/ask", { question });
        const answer = res.data.answer || "No answer found.";

        this.messages = this.messages.filter((msg) => msg.id !== "loading");
        this.messages.push({
          id: Date.now() + 1,
          text: answer,
          type: "ai",
        });
        this.scrollToBottom();
      } catch (error) {
        this.messages = this.messages.filter((msg) => msg.id !== "loading");
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error sending message",
            icon: "BellIcon",
            variant: "danger",
            text: error?.response?.data?.message,
          },
        });
      } finally {
        this.isLoading = false;
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const body = this.$refs.chatBody;
        if (body) {
          body.scrollTop = body.scrollHeight;
          this.showScrollButton = false;
        }
      });
    },

    handleScroll() {
      const body = this.$refs.chatBody;
      if (!body) return;
      this.showScrollButton =
        body.scrollHeight - body.scrollTop - body.clientHeight > 50;
    },
  },
};
</script>

<style scoped>
.chatbot-container {
  position: fixed;
  bottom: 20px;
  right: 27px;
  width: 420px;
  height: 55vh;
  background: #fff;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.2);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  border-radius: 12px 12px 0 0;
  overflow: hidden;
}

/* Header */
.chatbot-header {
  background: linear-gradient(118deg, #7367f0, rgba(115, 103, 240, 0.7));
  color: #fff;
  padding: 12px 16px;
  font-weight: bold;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 22px;
  color: #fff;
  cursor: pointer;
}

/* Body */
.chatbot-body {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  scrollbar-width: thin;
  scrollbar-color: #007bff #f1f1f1;
}

.chatbot-body::-webkit-scrollbar {
  width: 6px;
}

.chatbot-body::-webkit-scrollbar-thumb {
  background-color: #007bff;
  border-radius: 4px;
}

/* Messages */
.chat-message {
  margin-bottom: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  max-width: 85%;
  word-break: break-word;
  line-height: 1.5;
  font-size: 0.95rem;
}

.chat-message.user {
  align-self: flex-end;
  background: #007bff;
  color: #fff;
}

.chat-message.ai {
  align-self: flex-start;
  background: #f5f5f5;
  color: #000;
}

/* Footer */
.chatbot-footer {
  display: flex;
  padding: 10px;
  border-top: 1px solid #ddd;
  background: #fafafa;
}

.chatbot-footer input {
  flex: 1;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid #ccc;
  margin-right: 8px;
  font-size: 0.95rem;
}

.chatbot-footer button {
  padding: 8px 14px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
}

/* Loading dots */
.loading-dots {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 20px;
}

.loading-dots span {
  width: 6px;
  height: 6px;
  background: #bbb;
  border-radius: 50%;
  animation: blink 1.4s infinite both;
}

.loading-dots span:nth-child(1) {
  animation-delay: 0s;
}
.loading-dots span:nth-child(2) {
  animation-delay: 0.2s;
}
.loading-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes blink {
  0%, 80%, 100% {
    opacity: 0;
  }
  40% {
    opacity: 1;
  }
}

/* Scroll Button */
.scroll-bottom-btn {
  position: absolute;
  bottom: 70px;
  right: 20px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  font-size: 18px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Slide Animation */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.slide-enter {
  transform: translateX(100%);
  opacity: 0;
}
.slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* ✅ Mobile Responsive Styles */
@media (max-width: 768px) {
  .chatbot-container {
    width: 95%;
    height: 60vh;
    right: 2.5%;
    bottom: 10px;
    border-radius: 10px;
  }

  .chatbot-header {
    font-size: 1rem;
    padding: 10px;
  }

  .chatbot-footer input {
    font-size: 0.9rem;
  }

  .chatbot-footer button {
    padding: 8px 10px;
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .chatbot-container {
    width: 100%;
    height: 100vh;
    bottom: 0;
    right: 0;
    left: 0;
    top: 0;
    border-radius: 0;
    z-index: 3000;
  }

  .chatbot-header {
    font-size: 1rem;
    padding: 14px;
  }

  .chatbot-footer {
    padding: 12px;
  }

  .chatbot-footer input {
    font-size: 0.9rem;
  }

  .chatbot-footer button {
    font-size: 0.9rem;
    padding: 8px 12px;
  }
}
</style>
